import { createContext, useContext, useState } from 'react';
import useMeasure from 'react-use-measure';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import { byVideoType } from 'src/lib/utils';
import {
  CoverImage,
  CoverVideo,
  DarkModeGradientOverlay,
  EmbeddedVideoPlayer,
  LightModeTextStackBackground,
  TextStack,
  VideoCardShadow,
  VideoCardWrapper,
} from './CmsEmbeddedVideoCard.styled';

interface EmbeddedVideoCardContextType {
  isVideoPlaying: boolean;
  setIsVideoPlaying: (isPlaying: boolean) => void;
}

const EmbeddedVideoCardContext =
  createContext<EmbeddedVideoCardContextType | null>(null);

export const EmbeddedVideoCardProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  return (
    <EmbeddedVideoCardContext.Provider
      value={{ isVideoPlaying, setIsVideoPlaying }}
    >
      {children}
    </EmbeddedVideoCardContext.Provider>
  );
};

export const useEmbeddedVideoCard = (): EmbeddedVideoCardContextType => {
  const context = useContext(EmbeddedVideoCardContext);
  if (!context) {
    return {
      isVideoPlaying: false,
      setIsVideoPlaying: () => {
        // eslint-disable-next-line no-console
        console.warn('No context found');
      },
    };
  }
  return context;
};

const VideoCard = ({ isVideoPlaying, sys, fields }) => {
  const [ref, { height }] = useMeasure();
  // TODO: videoPlayer should be replaced with the embedded appearance asset content type
  const coverMedia = fields?.videoPlayer?.fields?.coverMedia?.[0];

  const isVideo = coverMedia?.fields?.file?.contentType?.includes('video');
  const sortedVideos =
    fields?.videoPlayer?.fields?.coverMedia?.sort(byVideoType);

  const src = coverMedia?.fields?.file?.url;
  const alt =
    coverMedia?.fields?.file?.description ??
    coverMedia?.fields?.file?.title ??
    '';
  return (
    <VideoCardShadow
      className='video-card-shadow'
      rainbowGlowEffect={fields?.rainbowGlowEffect ?? false}
    >
      <VideoCardWrapper
        isVideoPlaying={isVideoPlaying}
        className='video-card-wrapper'
        sx={{
          // we measure the height of the text stack and use that to set the height of the overlay video
          height: `${height * 1.75}px`,
        }}
      >
        <EmbeddedVideoPlayer
          className='embedded-video-player'
          url={fields?.videoPlayer?.fields?.url}
          playing={isVideoPlaying}
        />
        <>
          <DarkModeGradientOverlay />
          {isVideo && (
            <CoverVideo
              className='cover-video'
              isLazy={fields?.videoPlayer?.fields?.isLazy}
              width='100%'
              height='100%'
              sources={sortedVideos?.map((asset) => {
                return {
                  src: asset?.fields?.file?.url,
                  type: asset?.fields?.file?.contentType,
                };
              })}
            />
          )}
          {!isVideo && (
            <CoverImage
              className='cover-image'
              src={`${src}?fm=webp&w=1388&q=70`}
              isLazy={fields?.videoPlayer?.fields?.isLazy}
              sources={{
                xs: `${src}?fm=webp&w=617&q=70 1x, ${src}?fm=webp&w=1234&q=70 2x`,
                sm: `${src}?fm=webp&w=951&q=70 1x, ${src}?fm=webp&w=1902&q=70 2x`,
                md: `${src}?fm=webp&w=1215&q=70 1x, ${src}?fm=webp&w=2430&q=70 2x`,
                lg: `${src}?fm=webp&w=1146&q=70 1x, ${src}?fm=webp&w=2292&q=70 2x`,
                xl: `${src}?fm=webp&w=1388&q=70 1x, ${src}?fm=webp&w=2776&q=70 2x`,
              }}
              width={1388}
              height={736}
              alt={alt}
            />
          )}
          <TextStack className='text-stack' ref={ref}>
            <RichTextSwitch
              sys={sys}
              content={fields?.body}
              nodes={{
                h2: {
                  variant: 'h2',
                  component: 'h3',
                  fieldId: 'body',
                },
                h6: {
                  variant: 'h6',
                  component: 'h2',
                  fieldId: 'body',
                },
                p: {
                  variant: 'p',
                  fieldId: 'body',
                },
              }}
            />
            <LightModeTextStackBackground className='light-mode-text-stack-background' />
          </TextStack>
        </>
      </VideoCardWrapper>
    </VideoCardShadow>
  );
};

export default VideoCard;
